import React, { useState, useRef, useEffect } from 'react';
import { Html5Qrcode, Html5QrcodeSupportedFormats } from 'html5-qrcode';
import { itemsAPI } from '../../api';
import RelatedItemsDisplay from './RelatedItemsDisplay';
import ItemSearchComponent from '../ui/ItemSearchComponent';

// The <div> html5-qrcode renders the live camera preview + scan overlay into.
const SCANNER_ELEMENT_ID = 'scanner-container';

// Barcode symbologies we care about (retail 1D + QR). Restricting the set makes
// live decoding noticeably faster and more reliable than leaving every format on.
const BARCODE_FORMATS = [
  Html5QrcodeSupportedFormats.QR_CODE,
  Html5QrcodeSupportedFormats.CODE_128,
  Html5QrcodeSupportedFormats.CODE_39,
  Html5QrcodeSupportedFormats.CODE_93,
  Html5QrcodeSupportedFormats.EAN_13,
  Html5QrcodeSupportedFormats.EAN_8,
  Html5QrcodeSupportedFormats.UPC_A,
  Html5QrcodeSupportedFormats.UPC_E,
  Html5QrcodeSupportedFormats.ITF,
  Html5QrcodeSupportedFormats.CODABAR,
];

const SalesmanScanner = () => {
  const [isScanning, setIsScanning] = useState(false);
  const [scannedBarcode, setScannedBarcode] = useState(null);
  const [relatedItems, setRelatedItems] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [permissionDenied, setPermissionDenied] = useState(false);
  const [isRequestingPermission, setIsRequestingPermission] = useState(false);
  const [isManuallySelected, setIsManuallySelected] = useState(false);

  // Camera controls (zoom / torch / tap-to-focus). Populated by feature-detecting
  // the live video track — each control only surfaces where the device supports it.
  const [cameraControls, setCameraControls] = useState({ zoom: null, torch: false, focus: false });
  const [zoomValue, setZoomValue] = useState(1);
  const [torchOn, setTorchOn] = useState(false);
  const [focusRing, setFocusRing] = useState({ x: 0, y: 0, visible: false });

  // Live html5-qrcode instance + a guard so the (repeatedly-fired) success
  // callback is only acted on once per scan.
  const html5QrcodeRef = useRef(null);
  const hasScannedRef = useRef(false);

  // Stop and release the live scanner. Safe to call when nothing is running.
  const teardownScanner = async () => {
    const instance = html5QrcodeRef.current;
    if (!instance) return;
    html5QrcodeRef.current = null;
    try {
      await instance.stop();
    } catch (stopError) {
      console.log('scanner stop skipped:', stopError);
    }
    try {
      instance.clear();
    } catch (clearError) {
      console.log('scanner clear skipped:', clearError);
    }
  };

  // ---- Camera controls, feature-detected against the running video track ----

  // html5-qrcode injects its own <video> into the scan region; reach the live
  // MediaStreamTrack through it so we can read capabilities / apply constraints.
  const getVideoTrack = () => {
    const video = document.querySelector(`#${SCANNER_ELEMENT_ID} video`);
    const stream = video && video.srcObject;
    if (stream && typeof stream.getVideoTracks === 'function') {
      return stream.getVideoTracks()[0] || null;
    }
    return null;
  };

  const resetCameraControls = () => {
    setCameraControls({ zoom: null, torch: false, focus: false });
    setTorchOn(false);
    setZoomValue(1);
  };

  // Inspect what the running camera actually supports and expose only those controls.
  const detectCameraControls = () => {
    const track = getVideoTrack();
    if (!track || typeof track.getCapabilities !== 'function') {
      resetCameraControls();
      return;
    }
    let caps = {};
    let settings = {};
    try { caps = track.getCapabilities() || {}; } catch { caps = {}; }
    try { settings = (typeof track.getSettings === 'function' ? track.getSettings() : {}) || {}; } catch { settings = {}; }

    const zoom =
      caps.zoom && typeof caps.zoom.max === 'number' && caps.zoom.max > (caps.zoom.min ?? 1)
        ? { min: caps.zoom.min ?? 1, max: caps.zoom.max, step: caps.zoom.step || 0.1 }
        : null;
    const torch = caps.torch === true;
    const focus =
      'pointsOfInterest' in caps ||
      (Array.isArray(caps.focusMode) &&
        (caps.focusMode.includes('single-shot') || caps.focusMode.includes('manual')));

    setCameraControls({ zoom, torch, focus });
    if (zoom) setZoomValue(typeof settings.zoom === 'number' ? settings.zoom : zoom.min);
    setTorchOn(settings.torch === true);
  };

  const applyZoom = async (value) => {
    setZoomValue(value);
    const track = getVideoTrack();
    if (!track) return;
    try {
      await track.applyConstraints({ advanced: [{ zoom: value }] });
    } catch (zoomError) {
      console.log('zoom apply failed:', zoomError);
    }
  };

  const toggleTorch = async () => {
    const track = getVideoTrack();
    if (!track) return;
    const next = !torchOn;
    try {
      await track.applyConstraints({ advanced: [{ torch: next }] });
      setTorchOn(next);
    } catch (torchError) {
      console.log('torch apply failed:', torchError);
    }
  };

  const handleTapToFocus = async (event) => {
    const track = getVideoTrack();
    if (!track) return;

    const rect = event.currentTarget.getBoundingClientRect();
    const offsetX = event.clientX - rect.left;
    const offsetY = event.clientY - rect.top;

    // Ripple a focus ring where the user tapped.
    setFocusRing({ x: offsetX, y: offsetY, visible: true });
    window.setTimeout(() => setFocusRing((prev) => ({ ...prev, visible: false })), 700);

    let caps = {};
    try { caps = track.getCapabilities() || {}; } catch { caps = {}; }

    const constraint = {};
    if ('pointsOfInterest' in caps) {
      constraint.pointsOfInterest = [{
        x: Math.min(Math.max(offsetX / rect.width, 0), 1),
        y: Math.min(Math.max(offsetY / rect.height, 0), 1),
      }];
    }
    if (Array.isArray(caps.focusMode)) {
      if (caps.focusMode.includes('single-shot')) constraint.focusMode = 'single-shot';
      else if (caps.focusMode.includes('manual')) constraint.focusMode = 'manual';
    }
    if (Object.keys(constraint).length === 0) return;

    try {
      await track.applyConstraints({ advanced: [constraint] });
    } catch (focusError) {
      console.log('tap-to-focus failed:', focusError);
    }
  };

  // Release the camera if the user navigates away mid-scan.
  useEffect(() => {
    return () => {
      teardownScanner();
    };
  }, []);

  // Lock background scroll while the full-screen scanner is open. We intentionally
  // leave the browser chrome (iOS Safari's toolbar tint) alone — trying to steer it
  // via theme-color was unreliable on iOS and reached into the rest of the app.
  useEffect(() => {
    if (!isScanning) return undefined;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, [isScanning]);

  const fetchRelatedItems = async (barcode) => {
    setIsLoading(true);
    setError(null);

    try {
      const response = await itemsAPI.getRelatedByBarcode(barcode);
      setRelatedItems(response.data);

      if (response.data && response.data.length > 0) {
        setTimeout(() => {
          const relatedItemsSection = document.getElementById('related-items-section');
          if (relatedItemsSection) {
            relatedItemsSection.scrollIntoView({ behavior: 'smooth', block: 'start' });
          }
        }, 100);
      }
    } catch (err) {
      console.error('Error fetching related items:', err);
      if (err.response?.status === 404) {
        setError('Item with this barcode not found');
      } else {
        setError('Failed to load related items. Please try again.');
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleScanSuccess = async (barcode) => {
    console.log('Barcode scanned:', barcode);
    setScannedBarcode(barcode);
    setIsManuallySelected(false);
    await teardownScanner();
    setIsScanning(false);
    resetCameraControls();
    await fetchRelatedItems(barcode);
  };

  const startScanning = async () => {
    setError(null);
    setPermissionDenied(false);
    setScannedBarcode(null);
    setRelatedItems([]);
    setIsManuallySelected(false);
    hasScannedRef.current = false;
    resetCameraControls();

    if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
      setError('Camera access not available. Please use HTTPS or a modern browser.');
      return;
    }

    // Open the full-screen scanner, then wait two frames for its scan region to
    // mount and lay out before html5-qrcode measures it.
    setIsScanning(true);
    await new Promise((resolve) =>
      requestAnimationFrame(() => requestAnimationFrame(resolve))
    );

    // Clear out any previous instance first.
    await teardownScanner();

    const onScanSuccess = (decodedText) => {
      if (hasScannedRef.current) return;
      hasScannedRef.current = true;
      handleScanSuccess(decodedText);
    };
    // Per-frame "no barcode in this frame yet" callbacks are normal noise.
    const onScanFailure = () => {};

    const config = {
      fps: 10,
      // A wide box suits horizontal 1D barcodes — a small square box misses them.
      qrbox: (viewfinderWidth, viewfinderHeight) => ({
        width: Math.max(Math.floor(viewfinderWidth * 0.8), 50),
        height: Math.max(Math.floor(viewfinderHeight * 0.5), 50),
      }),
    };

    try {
      const html5Qrcode = new Html5Qrcode(SCANNER_ELEMENT_ID, {
        formatsToSupport: BARCODE_FORMATS,
        // Uses the fast native BarcodeDetector where it exists (Android/Chrome);
        // falls back to the bundled ZXing engine elsewhere (e.g. iOS).
        experimentalFeatures: { useBarCodeDetectorIfSupported: true },
        verbose: false,
      });
      html5QrcodeRef.current = html5Qrcode;

      try {
        // Back camera on phones. The live feed keeps continuous autofocus,
        // which is what makes real-world barcodes actually resolve.
        await html5Qrcode.start({ facingMode: 'environment' }, config, onScanSuccess, onScanFailure);
      } catch (backCameraError) {
        const message = String(backCameraError?.message || backCameraError || '');
        if (/NotAllowed|Permission|denied/i.test(message)) {
          throw backCameraError; // a permission problem — retrying won't help
        }
        // No back camera (e.g. a desktop) — fall back to any available camera.
        console.log('Back camera unavailable, trying default camera:', backCameraError);
        await html5Qrcode.start({ facingMode: 'user' }, config, onScanSuccess, onScanFailure);
      }

      // Surface whatever camera controls (zoom / torch / tap-to-focus) this
      // device actually supports, once the track is fully live.
      window.setTimeout(detectCameraControls, 600);
    } catch (err) {
      console.error('Error starting scanner:', err);
      await teardownScanner();
      setIsScanning(false);

      const message = String(err?.message || err || '');
      if (/NotAllowed|Permission|denied/i.test(message)) {
        setPermissionDenied(true);
        setError('Camera permission denied. Please allow camera access for this site, then try again.');
      } else if (/NotFound|Requested device not found/i.test(message)) {
        setError('No camera found. Please connect a camera and try again.');
      } else if (/NotReadable|in use/i.test(message)) {
        setError('The camera is already in use by another app. Close other camera apps and try again.');
      } else {
        setError('Failed to start the camera. Please check permissions and try again.');
      }
    }
  };

  const stopScanning = async () => {
    await teardownScanner();
    setIsScanning(false);
    resetCameraControls();
  };

  const scanAgain = () => {
    setScannedBarcode(null);
    setRelatedItems([]);
    setError(null);
    setPermissionDenied(false);
    setIsManuallySelected(false);
    startScanning();
  };

  const handleGrantPermission = async () => {
    setIsRequestingPermission(true);
    setError(null);
    setPermissionDenied(false);
    try {
      await startScanning();
    } finally {
      setIsRequestingPermission(false);
    }
  };

  const handleItemSelect = (item) => {
    const barcode = item?.Barcode;
    if (!barcode) {
      alert('Item has no barcode');
      return;
    }
    setScannedBarcode(barcode);
    setIsManuallySelected(true);
    setError(null);
    setPermissionDenied(false);
    fetchRelatedItems(barcode);
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-4 sm:py-8">
        {/* Item Search Section */}
        <div className="bg-white rounded-lg shadow-sm border mb-6">
          <div className="p-4 sm:p-6">
            <h2 className="text-lg font-medium text-gray-900 mb-4">Search Items</h2>
            <ItemSearchComponent
              placeholder="Search by item name or barcode..."
              onItemSelect={handleItemSelect}
              showBarcodeSearch={true}
              showNameSearch={false}
              autoFocus={false}
              showOutOfStock={true}
              inputType="number"
              className="mb-0"
            />
          </div>
        </div>

        {/* Scanner Section */}
        <div className="bg-white rounded-lg shadow-sm border mb-6">
          <div className="p-4 sm:p-6">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between mb-4 gap-4">
              <h2 className="text-lg font-medium text-gray-900">Scan Barcode</h2>
              <button
                onClick={startScanning}
                className="inline-flex items-center justify-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 w-full sm:w-auto"
              >
                <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z" />
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 13a3 3 0 11-6 0 3 3 0 016 0z" />
                </svg>
                Start Camera
              </button>
            </div>

            {/* Idle prompt — opens the full-screen scanner */}
            {!scannedBarcode && (
              <button
                type="button"
                onClick={startScanning}
                className="w-full rounded-lg border-2 border-dashed border-gray-300 bg-gray-50 hover:bg-gray-100 transition-colors p-8 flex flex-col items-center justify-center text-center"
              >
                <svg className="h-12 w-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z" />
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 13a3 3 0 11-6 0 3 3 0 016 0z" />
                </svg>
                <p className="mt-2 text-sm text-gray-500">Tap to open the scanner — zoom &amp; torch open with the camera</p>
              </button>
            )}

            {/* Scanned Barcode Display - Only show for actual scans */}
            {scannedBarcode && !isManuallySelected && (
              <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                <div className="flex items-center justify-between">
                  <div className="flex-1">
                    <h3 className="text-sm font-medium text-green-900">Barcode Successfully Scanned!</h3>
                    <p className="text-green-700 font-mono text-lg mt-1">{scannedBarcode}</p>
                    <p className="text-xs text-green-600 mt-1">Camera stopped automatically after successful scan</p>
                  </div>
                  <button
                    onClick={scanAgain}
                    className="inline-flex items-center px-4 py-2 border border-green-300 shadow-sm text-sm leading-4 font-medium rounded-md text-green-700 bg-white hover:bg-green-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 ml-4"
                  >
                    <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                    </svg>
                    Scan Another Item
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Related Items Display */}
        {scannedBarcode && (
          <div id="related-items-section">
            <RelatedItemsDisplay
              items={relatedItems}
              scannedBarcode={scannedBarcode}
              isLoading={isLoading}
              error={error}
            />
          </div>
        )}
      </div>

      {/* Full-screen camera scanner */}
      {isScanning && (
        <div className="fixed inset-0 z-50 bg-black flex flex-col">
          {/* Top bar */}
          <div
            className="flex items-center justify-between px-4 py-3 text-white"
            style={{ paddingTop: 'calc(env(safe-area-inset-top, 0px) + 0.75rem)' }}
          >
            <button
              onClick={stopScanning}
              aria-label="Close scanner"
              className="inline-flex items-center justify-center w-10 h-10 rounded-full bg-white/10 hover:bg-white/20"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
            <span className="text-sm font-medium">Scan Barcode</span>
            {cameraControls.torch ? (
              <button
                onClick={toggleTorch}
                aria-pressed={torchOn}
                aria-label="Toggle torch"
                className={`inline-flex items-center justify-center w-10 h-10 rounded-full ${
                  torchOn ? 'bg-yellow-400 text-yellow-900' : 'bg-white/10 hover:bg-white/20 text-white'
                }`}
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                </svg>
              </button>
            ) : (
              <span className="w-10" />
            )}
          </div>

          {/* Live camera preview (tap to focus where supported) */}
          <div
            className="relative flex-1 overflow-hidden flex items-center justify-center"
            onClick={cameraControls.focus ? handleTapToFocus : undefined}
            style={cameraControls.focus ? { cursor: 'crosshair' } : undefined}
          >
            {/* html5-qrcode injects the live camera preview + scan overlay here */}
            <div id={SCANNER_ELEMENT_ID} className="w-full" />

            {focusRing.visible && (
              <span
                className="pointer-events-none absolute w-16 h-16 -ml-8 -mt-8 rounded-full border-2 border-white animate-ping"
                style={{ left: `${focusRing.x}px`, top: `${focusRing.y}px` }}
              />
            )}
          </div>

          {/* Bottom controls */}
          <div
            className="px-6 pt-4 text-white"
            style={{ paddingBottom: 'calc(env(safe-area-inset-bottom, 0px) + 1.25rem)' }}
          >
            <div className="text-center mb-4">
              <span className="inline-flex items-center px-4 py-2 rounded-full text-sm font-medium bg-white/15">
                <span className="w-2 h-2 bg-green-400 rounded-full mr-2 animate-pulse" />
                Scanning… point at the barcode
              </span>
              <p className="text-xs text-white/70 mt-2">
                {cameraControls.focus
                  ? 'Tap the preview to focus · fill the box with the barcode'
                  : 'Hold steady and fill the box with the barcode'}
              </p>
            </div>

            {cameraControls.zoom && (
              <div className="flex items-center gap-3 max-w-lg mx-auto">
                <svg className="w-5 h-5 text-white/80 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-4.35-4.35M19 11a8 8 0 11-16 0 8 8 0 0116 0z" />
                </svg>
                <input
                  type="range"
                  min={cameraControls.zoom.min}
                  max={cameraControls.zoom.max}
                  step={cameraControls.zoom.step}
                  value={zoomValue}
                  onChange={(e) => applyZoom(Number(e.target.value))}
                  className="flex-1 accent-blue-500"
                  aria-label="Camera zoom"
                />
                <span className="text-xs text-white/80 w-10 text-right">{Number(zoomValue).toFixed(1)}×</span>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Error Modal */}
      {error && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[60] p-4">
          <div
            role="dialog"
            aria-modal="true"
            className="bg-white rounded-xl shadow-xl max-w-md w-full mx-4 p-6 text-center"
          >
            <div className="mx-auto flex items-center justify-center w-14 h-14 rounded-full bg-red-100 mb-4">
              <svg className="w-7 h-7 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>

            <h3 className="text-lg font-semibold text-gray-900">
              {permissionDenied
                ? 'Camera Permission Needed'
                : /not\s*found/i.test(error)
                  ? 'Item Not Found'
                  : 'Scanner Error'}
            </h3>
            <p className="text-sm text-gray-600 mt-2">{error}</p>

            {permissionDenied ? (
              <div className="mt-6 flex flex-col gap-2">
                <button
                  onClick={handleGrantPermission}
                  disabled={isRequestingPermission}
                  className="inline-flex items-center justify-center px-4 py-2.5 text-sm font-medium rounded-lg text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isRequestingPermission ? 'Requesting Permission…' : 'Grant Camera Permission'}
                </button>
                <button
                  onClick={() => setError(null)}
                  className="inline-flex items-center justify-center px-4 py-2.5 text-sm font-medium rounded-lg text-gray-700 bg-gray-100 hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-400"
                >
                  Close
                </button>
              </div>
            ) : (
              <button
                onClick={() => setError(null)}
                className="mt-6 w-full inline-flex items-center justify-center px-4 py-2.5 text-sm font-medium rounded-lg text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
              >
                OK
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default SalesmanScanner;
